import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/react/react/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import data from '../../packages/react/src/Overlay/Overlay.docs.json';
import DeprecationBanner from '../components/DeprecationBanner';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const ComponentProps = makeShortcode("ComponentProps");
const ComponentChecklist = makeShortcode("ComponentChecklist");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <DeprecationBanner replacementUrl={'/components/overlay/react/latest'} mdxType="DeprecationBanner" />
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {Overlay} from '@primer/react'
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{` component handles all behaviors needed by overlay UIs as well as the common styles that all overlays should have. `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{` is the base component for many of our overlay-type components. Overlays use shadows to express elevation.`}</p>
    <p><strong parentName="p">{`Behaviors include:`}</strong></p>
    <ul>
      <li parentName="ul">{`Rendering the overlay in a React Portal so that it always renders on top of other content on the page`}</li>
      <li parentName="ul">{`Trapping focus`}</li>
      <li parentName="ul">{`Calling a user provided function when the user presses `}<inlineCode parentName="li">{`Escape`}</inlineCode></li>
      <li parentName="ul">{`Calling a user provided function when the user clicks outside of the container`}</li>
      <li parentName="ul">{`Focusing either user provided element, or the first focusable element in the container when it is opened`}</li>
      <li parentName="ul">{`Returning focus to an element when container is closed`}</li>
    </ul>
    <h2>{`Example`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const Example = () => {
  // you must manage your own open state
  const [isOpen, setIsOpen] = React.useState(false)
  const noButtonRef = React.useRef(null)
  const anchorRef = React.useRef(null)
  return (
    <>
      <Button ref={anchorRef} onClick={() => setIsOpen(!isOpen)}>
        open overlay
      </Button>
      {/* be sure to conditionally render the Overlay. This helps with performance and is required. */}
      {isOpen && (
        <Overlay
          initialFocusRef={noButtonRef}
          returnFocusRef={anchorRef}
          ignoreClickRefs={[anchorRef]}
          onEscape={() => setIsOpen(!isOpen)}
          onClickOutside={() => setIsOpen(false)}
          aria-labelledby="title"
        >
          <Box display="flex" flexDirection="column" p={2}>
            <Text id="title">Are you sure you would like to delete this item?</Text>
            <Button>yes</Button>
            <Button ref={noButtonRef}>no</Button>
          </Box>
        </Overlay>
      )}
    </>
  )
}

render(<Example />)
`}</code></pre>
    <h2>{`Accessibility considerations`}</h2>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` must either have:`}
        <ul parentName="li">
          <li parentName="ul">{`A value set for the `}<inlineCode parentName="li">{`aria-labelledby`}</inlineCode>{` attribute that refers to a visible title.`}</li>
          <li parentName="ul">{`An `}<inlineCode parentName="li">{`aria-label`}</inlineCode>{` attribute`}</li>
        </ul>
      </li>
      <li parentName="ul">{`If the `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` should also have a longer description, use `}<inlineCode parentName="li">{`aria-describedby`}</inlineCode></li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` component has a `}<inlineCode parentName="li">{`role="dialog"`}</inlineCode>{` set on it, if you are using `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` for alerts, you can pass in `}<inlineCode parentName="li">{`role="alertdialog"`}</inlineCode>{` instead. Please read the `}<a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.1/#alertdialog"
        }}>{`W3C guidelines`}</a>{` to determine which role is best for your use case`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` component has `}<inlineCode parentName="li">{`aria-modal`}</inlineCode>{` set to `}<inlineCode parentName="li">{`true`}</inlineCode>{` by default and should not be overridden as all `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{`s behave as modals.`}</li>
    </ul>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_roles_states_props"
      }}>{`W3C accessibility recommendations for modals`}</a>{`.`}</p>
    <h2>{`Positioning`}</h2>
    <p><inlineCode parentName="p">{`Overlay`}</inlineCode>{` renders its `}<inlineCode parentName="p">{`children`}</inlineCode>{` within a div positioned absolutely within a portal within the default portal root. The overlay will not update its positioning if the portal root's positioning changes (e.g., if the portal root is statically positioned after some DOM element that dynamically resizes). You may consider using the `}<a parentName="p" {...{
        "href": "/AnchoredOverlay"
      }}>{`AnchoredOverlay`}</a>{` component or `}<a parentName="p" {...{
        "href": "/Portal#customizing-the-portal-root"
      }}>{`customizing the portal root`}</a>{` to achieve different positioning behavior.`}</p>
    <p>{`The position of the Overlay can be customized by using the `}<inlineCode parentName="p">{`position`}</inlineCode>{` prop in conjunction with the `}<inlineCode parentName="p">{`top|left|right|bottom`}</inlineCode>{` props.`}</p>
    <h2>{`Props`}</h2>
    <ComponentProps data={data} mdxType="ComponentProps" />
    <h2>{`Status`}</h2>
    <ComponentChecklist items={{
      propsDocumented: true,
      noUnnecessaryDeps: true,
      adaptsToThemes: true,
      adaptsToScreenSizes: true,
      fullTestCoverage: false,
      usedInProduction: false,
      usageExamplesDocumented: false,
      designReviewed: false,
      a11yReviewed: false,
      stableApi: false,
      addressedApiFeedback: false,
      hasDesignGuidelines: false,
      hasFigmaComponent: false
    }} mdxType="ComponentChecklist" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      